// Gantt Resource View Component - Resource allocation visualization
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { User, Cpu, Calendar, Clock } from 'lucide-react';
import { Task, Resource, User as UserType, Machine, ViewMode } from '../types/gantt.types';
import { calculateTaskPosition, getDateRange, formatDateHeader } from '../utils/ganttCalculations';

interface GanttResourceViewProps {
  tasks: Task[];
  resources: Resource[];
  users: UserType[];
  machines: Machine[];
  viewMode: ViewMode;
  zoom: number;
}

export const GanttResourceView: React.FC<GanttResourceViewProps> = ({
  tasks,
  resources,
  users,
  machines,
  viewMode,
  zoom
}) => {
  const { startDate, endDate, totalDays } = getDateRange(tasks, viewMode);
  const dayWidth = (40 * zoom) / 100;
  const rowHeight = 50;
  const headerHeight = 60;

  // Calculate resource allocation
  const resourceAllocation = useMemo(() => {
    const allocation: Record<string, { tasks: Task[]; totalHours: number; capacity: number }> = {};

    // Initialize for users
    users.forEach(user => {
      allocation[`user-${user.id}`] = {
        tasks: [],
        totalHours: 0,
        capacity: user.capacity || 8
      };
    });

    // Initialize for machines
    machines.forEach(machine => {
      allocation[`machine-${machine.id}`] = {
        tasks: [],
        totalHours: 0,
        capacity: machine.capacity || 24
      };
    });

    // Assign tasks to resources
    tasks.forEach(task => {
      const taskResources = resources.filter(r => r.task_id === task.id);
      
      taskResources.forEach(resource => {
        if (resource.user_id) {
          const key = `user-${resource.user_id}`;
          if (allocation[key]) {
            allocation[key].tasks.push(task);
            allocation[key].totalHours += (resource.hours_per_day || 8) * task.duration;
          }
        }
        
        if (resource.machine_id) {
          const key = `machine-${resource.machine_id}`;
          if (allocation[key]) {
            allocation[key].tasks.push(task);
            allocation[key].totalHours += (resource.hours_per_day || 24) * task.duration;
          }
        }
      });

      // Also check assigned_to field for users
      task.assigned_to?.forEach(userId => {
        const key = `user-${userId}`;
        if (allocation[key] && !allocation[key].tasks.find(t => t.id === task.id)) {
          allocation[key].tasks.push(task);
          allocation[key].totalHours += (task.estimated_hours || 8 * task.duration);
        }
      });
    });

    return allocation;
  }, [tasks, resources, users, machines]);

  // Render resource row
  const renderResourceRow = (
    resourceId: string,
    resourceName: string,
    resourceType: 'user' | 'machine',
    index: number
  ) => {
    const allocation = resourceAllocation[resourceId];
    if (!allocation) return null;

    const y = index * rowHeight + headerHeight;
    const utilization = Math.min(100, (allocation.totalHours / (allocation.capacity * totalDays)) * 100);

    return (
      <g key={resourceId} className="resource-row">
        {/* Row background */}
        <rect
          x={0}
          y={y}
          width={totalDays * dayWidth}
          height={rowHeight}
          fill={index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}
          stroke="rgba(255,255,255,0.05)"
          strokeWidth={0.5}
        />

        {/* Resource tasks */}
        {allocation.tasks.map(task => {
          const { x, width } = calculateTaskPosition(task, startDate, dayWidth);
          const taskY = y + 10;
          
          return (
            <g key={`${resourceId}-${task.id}`}>
              <rect
                x={x}
                y={taskY}
                width={width}
                height={30}
                rx={4}
                fill={utilization > 100 ? '#ef4444' : utilization > 80 ? '#f59e0b' : '#10b981'}
                fillOpacity={0.8}
                className="cursor-pointer hover:fill-opacity-100 transition-all"
              />
              <text
                x={x + 8}
                y={taskY + 20}
                fill="white"
                fontSize="11"
                className="pointer-events-none select-none"
              >
                {task.name.length > 15 ? `${task.name.substring(0, 15)}...` : task.name}
              </text>
            </g>
          );
        })}

        {/* Utilization indicator */}
        <rect
          x={0}
          y={y + rowHeight - 4}
          width={(totalDays * dayWidth) * (utilization / 100)}
          height={4}
          fill={utilization > 100 ? '#ef4444' : utilization > 80 ? '#f59e0b' : '#10b981'}
          fillOpacity={0.5}
        />
      </g>
    );
  };

  // Render timeline header
  const renderHeader = () => {
    const headers: JSX.Element[] = [];
    const currentDate = new Date(startDate);
    
    for (let i = 0; i < totalDays; i++) {
      const x = i * dayWidth;
      const dateStr = formatDateHeader(currentDate, viewMode);
      const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
      const isToday = currentDate.toDateString() === new Date().toDateString();
      
      headers.push(
        <g key={i}>
          <rect
            x={x}
            y={0}
            width={dayWidth}
            height={headerHeight}
            fill={isWeekend ? 'rgba(255,255,255,0.02)' : 'transparent'}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth={0.5}
          />
          {isToday && (
            <rect
              x={x}
              y={0}
              width={dayWidth}
              height={headerHeight + (users.length + machines.length) * rowHeight}
              fill="rgba(251,146,60,0.1)"
              className="pointer-events-none"
            />
          )}
          <text
            x={x + dayWidth / 2}
            y={30}
            fill="white"
            fontSize="12"
            textAnchor="middle"
            className="select-none"
          >
            {dateStr}
          </text>
        </g>
      );
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return headers;
  };

  const totalWidth = totalDays * dayWidth;
  const totalHeight = headerHeight + (users.length + machines.length) * rowHeight;

  return (
    <div className="flex h-full">
      {/* Resource names sidebar */}
      <div className="w-48 bg-zinc-900 border-r border-zinc-800 flex-shrink-0">
        <div className="h-[60px] border-b border-zinc-800 flex items-center px-4">
          <h3 className="text-sm font-semibold text-white">Zasoby</h3>
        </div>
        
        {/* Users section */}
        {users.length > 0 && (
          <>
            <div className="px-4 py-2 bg-zinc-800/50">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-gray-400 uppercase">Pracownicy</span>
              </div>
            </div>
            {users.map((user, index) => {
              const allocation = resourceAllocation[`user-${user.id}`];
              const utilization = allocation 
                ? Math.min(100, (allocation.totalHours / (allocation.capacity * totalDays)) * 100)
                : 0;
              
              return (
                <div
                  key={user.id}
                  className="h-[50px] border-b border-zinc-800 flex items-center justify-between px-4 hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold">
                      {user.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm text-white">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-medium ${
                      utilization > 100 ? 'text-red-400' : 
                      utilization > 80 ? 'text-yellow-400' : 
                      'text-green-400'
                    }`}>
                      {Math.round(utilization)}%
                    </p>
                  </div>
                </div>
              );
            })}
          </>
        )}
        
        {/* Machines section */}
        {machines.length > 0 && (
          <>
            <div className="px-4 py-2 bg-zinc-800/50">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-gray-400 uppercase">Maszyny</span>
              </div>
            </div>
            {machines.map((machine, index) => {
              const allocation = resourceAllocation[`machine-${machine.id}`];
              const utilization = allocation 
                ? Math.min(100, (allocation.totalHours / (allocation.capacity * totalDays)) * 100)
                : 0;
              
              return (
                <div
                  key={machine.id}
                  className="h-[50px] border-b border-zinc-800 flex items-center justify-between px-4 hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Cpu className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-white">{machine.name}</p>
                      <p className="text-xs text-gray-500">{machine.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-medium ${
                      machine.status === 'available' ? 'text-green-400' :
                      machine.status === 'busy' ? 'text-yellow-400' :
                      machine.status === 'maintenance' ? 'text-orange-400' :
                      'text-red-400'
                    }`}>
                      {machine.status}
                    </p>
                    <p className={`text-xs ${
                      utilization > 100 ? 'text-red-400' : 
                      utilization > 80 ? 'text-yellow-400' : 
                      'text-green-400'
                    }`}>
                      {Math.round(utilization)}%
                    </p>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-auto bg-zinc-900">
        <svg width={totalWidth} height={totalHeight}>
          {/* Grid and headers */}
          <g className="timeline-grid">
            {renderHeader()}
          </g>
          
          {/* Resource rows */}
          <g className="resource-rows">
            {users.map((user, index) => 
              renderResourceRow(`user-${user.id}`, user.name, 'user', index)
            )}
            {machines.map((machine, index) => 
              renderResourceRow(`machine-${machine.id}`, machine.name, 'machine', users.length + index)
            )}
          </g>
        </svg>
      </div>
    </div>
  );
};